import React, { useMemo, useCallback } from "react";
import { ListViewProductCard } from "@tvg/fuse-list-view-product-card";
import { setSearchToSessionStorage } from "client/api/data-layer-service";
import { saveForGoogleAnalyticsToSessionStorage } from "client/api/google-analytics-service";
import {
  addProductToSaveForLater,
  addProductToSessionStorage,
  getSavedProductsFromStorage,
  removeProductFromSaveForLater,
  removeProductFromSessionStorage,
} from "client/api/save-for-later-service";
import { Product } from "types/searchApi/response";
import { isEuro, makeSimple } from "client/components/utils/utils";
import {
  ANALYTICS_RECOMMENDED_PRODUCT,
  ANALYTICS_SUGGESTED_PRODUCT,
  DATA_LAYER,
} from "client/utils/constants";

export interface ProductComponentProps {
  product: Product;
  isSaved: boolean;
  isAutosuggest: boolean;
  brand: string;
  isSFLVeryIEHeaderEnabled: boolean;
}

export const isValidProduct = (product: Partial<Product>): product is Product =>
  Boolean(
    typeof product.product_id === "number" &&
      product.url &&
      product.title &&
      product.price,
  );

const ProductComponent: React.FC<ProductComponentProps> = ({
  product,
  isSaved,
  isAutosuggest,
  brand,
  isSFLVeryIEHeaderEnabled,
}: ProductComponentProps) => {
  const titleMemo = useMemo(
    () => (product.title ? makeSimple(product.title) : ""),
    [product.title],
  );

  const isUkBrand = brand !== "veryireland" || undefined;

  const imageMemo = useMemo(
    () => ({
      url: product.image?.url,
      altText: product.image?.alt_text
        ? makeSimple(product.image?.alt_text)
        : "",
    }),
    [product.image?.url, product.image?.alt_text],
  );

  const priceMemo = useMemo(
    () => ({
      current: product.price?.current,
      previous: product.price?.previous,
      saving: product.price?.saving,
      showCurrentPriceFrom: product.price?.currentFrom,
      showPreviousPriceFrom: product.price?.previousFrom,
      showSave: product.price?.displaySavePriceInd,
      isEuro: isEuro(product.price?.currencyCode),
    }),
    [product.price],
  );

  const saveForLaterCallback = useCallback(
    async (isSelected: boolean) => {
      const savedProducts = getSavedProductsFromStorage();
      const productIDString = product.product_id.toString();
      try {
        if (isSelected) {
          await addProductToSaveForLater(productIDString);
          addProductToSessionStorage(savedProducts, productIDString);
        } else {
          await removeProductFromSaveForLater(productIDString);
          removeProductFromSessionStorage(savedProducts, productIDString);
        }
      } catch (error) {
        console.error("Error updating save for later:", error);
      }
    },
    [product.product_id],
  );

  const sflMemo = useMemo(
    () => ({
      isSaved,
      saveCallback: saveForLaterCallback,
    }),
    [isSaved, saveForLaterCallback],
  );

  const onProductClickCallback = useCallback((): void => {
    saveForGoogleAnalyticsToSessionStorage(
      isAutosuggest
        ? ANALYTICS_SUGGESTED_PRODUCT
        : ANALYTICS_RECOMMENDED_PRODUCT,
    );
    setSearchToSessionStorage(DATA_LAYER.SUGGESTED_PRODUCT);
  }, [isAutosuggest]);

  if (!isValidProduct(product)) return null;
  return (
    <ListViewProductCard
      productUrl={product.url}
      brand={product.brand}
      title={titleMemo}
      image={imageMemo}
      price={priceMemo}
      saveForLater={isUkBrand || isSFLVeryIEHeaderEnabled ? sflMemo : undefined}
      dataTestId="suggested-product-card"
      onClick={onProductClickCallback}
    />
  );
};

export default React.memo(ProductComponent);
